import { call, delay, fork, put, takeLatest } from 'redux-saga/effects';
import { apiClient } from '../../../api/client';
import { AdminNotification } from '../../../types';
import { adminNotificationsActions } from './index';

const POLL_INTERVAL_MS = 45_000;

function* fetchList(
  action: ReturnType<typeof adminNotificationsActions.fetchListRequest>,
) {
  try {
    const limit = action.payload?.limit ?? 20;
    const unreadOnly = action.payload?.unreadOnly ?? false;
    const { data } = yield call(apiClient.get, '/api/admin/notifications', {
      params: { limit, unreadOnly: unreadOnly ? 'true' : 'false' },
    });
    yield put(
      adminNotificationsActions.fetchListSuccess(
        (data.notifications ?? []) as AdminNotification[],
      ),
    );
  } catch {
    yield put(adminNotificationsActions.fetchListFailure());
  }
}

function* fetchUnreadCount() {
  try {
    const { data } = yield call(apiClient.get, '/api/admin/notifications/unread-count');
    yield put(adminNotificationsActions.fetchUnreadCountSuccess(data.count as number));
  } catch {
    // Silent — badge may be stale until next poll
  }
}

function* pollNotifications() {
  while (true) {
    yield put(adminNotificationsActions.pollRequest());
    yield delay(POLL_INTERVAL_MS);
  }
}

function* handlePoll() {
  yield call(fetchUnreadCount);
}

function* markRead(action: ReturnType<typeof adminNotificationsActions.markReadRequest>) {
  try {
    const { data } = yield call(
      apiClient.patch,
      `/api/admin/notifications/${action.payload}/read`,
    );
    yield put(
      adminNotificationsActions.markReadSuccess(data.notification as AdminNotification),
    );
  } catch {
    // Ignore — user can retry
  }
}

function* markAllRead() {
  try {
    yield call(apiClient.patch, '/api/admin/notifications/read-all');
    yield put(adminNotificationsActions.markAllReadSuccess());
  } catch {
    // Ignore
  }
}

function* deleteNotification(action: ReturnType<typeof adminNotificationsActions.deleteRequest>) {
  try {
    yield call(apiClient.delete, `/api/admin/notifications/${action.payload}`);
    yield put(adminNotificationsActions.deleteSuccess(action.payload));
  } catch {
    // Ignore
  }
}

export function* adminNotificationsSaga() {
  yield fork(pollNotifications);
  yield takeLatest(adminNotificationsActions.fetchListRequest.type, fetchList);
  yield takeLatest(adminNotificationsActions.pollRequest.type, handlePoll);
  yield takeLatest(adminNotificationsActions.markReadRequest.type, markRead);
  yield takeLatest(adminNotificationsActions.markAllReadRequest.type, markAllRead);
  yield takeLatest(adminNotificationsActions.deleteRequest.type, deleteNotification);
}
